import type { SettingsType } from '../../../shared/api/pipeex/pcfSettingsClient';
import { normalizeMappingProjectKey } from '../../../shared/lib/mappingProjectIdentity';
import {
  buildUserProjectScopeKey,
  normalizeUserProjectUserId,
} from '../../../shared/lib/userProjectIdentity';
import {
  PROFILE_TRUST_STORAGE_KEY,
  setDurableStateValue,
} from '../../../shared/lib/durableState/durableState';

type MappingProfileProjectTrustSource = 'profile-load' | 'live-flow';
export type MappingSetupReadinessPolicy = 'legacy-conversion-bypass';
export type MappingRepairPersistenceStatus =
  | 'pending-save'
  | 'saved'
  | 'discarded-unsaved';

export interface MappingRepairPersistenceRecord {
  status: MappingRepairPersistenceStatus;
  updatedAt: string;
}

export interface MappingProfileProjectTrustRecord {
  userId: string;
  projectKey: string;
  setupCompleted: true;
  profileKey: string;
  settingsType: SettingsType | number | string;
  documentName: string | null;
  source: MappingProfileProjectTrustSource;
  confirmedAt: string;
  setupReadinessPolicy?: MappingSetupReadinessPolicy;
  repairPersistence?: MappingRepairPersistenceRecord;
}

interface MappingProfileProjectTrustStore {
  records: Record<string, MappingProfileProjectTrustRecord>;
  userProjectBindings: Record<string, string>;
  legacyProjectBindings: Record<string, string>;
  legacyClaimedProjects: Record<string, true>;
}

const emptyStore = (): MappingProfileProjectTrustStore => ({
  records: {},
  userProjectBindings: {},
  legacyProjectBindings: {},
  legacyClaimedProjects: {},
});

function getStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function normalizeProfileKey(profileKey: string | null | undefined): string | null {
  const normalized = profileKey?.trim().replace(/\//g, '\\').toLowerCase();
  return normalized || null;
}

export { normalizeMappingProjectKey };

export function buildMappingProfileProjectTrustKey(
  userId: string | null | undefined,
  projectKey: string | null | undefined,
  profileKey: string | null | undefined
): string | null {
  const scopeKey = buildUserProjectScopeKey(userId, projectKey);
  const normalizedProfileKey = normalizeProfileKey(profileKey);
  return scopeKey && normalizedProfileKey ? `${scopeKey}::${normalizedProfileKey}` : null;
}

function readStore(): MappingProfileProjectTrustStore {
  const storage = getStorage();
  if (!storage) {
    return emptyStore();
  }
  try {
    const parsed = JSON.parse(storage.getItem(PROFILE_TRUST_STORAGE_KEY) || '{}');
    if (!parsed || typeof parsed !== 'object') {
      return emptyStore();
    }
    const source = parsed as Record<string, unknown>;
    return {
      records: source.records && typeof source.records === 'object'
        ? source.records as MappingProfileProjectTrustStore['records']
        : {},
      userProjectBindings: source.userProjectBindings && typeof source.userProjectBindings === 'object'
        ? source.userProjectBindings as Record<string, string>
        : {},
      // v1 stored project-only bindings under projectBindings.
      legacyProjectBindings: source.legacyProjectBindings && typeof source.legacyProjectBindings === 'object'
        ? source.legacyProjectBindings as Record<string, string>
        : source.projectBindings && typeof source.projectBindings === 'object'
          ? source.projectBindings as Record<string, string>
          : {},
      legacyClaimedProjects: source.legacyClaimedProjects && typeof source.legacyClaimedProjects === 'object'
        ? source.legacyClaimedProjects as Record<string, true>
        : {},
    };
  } catch {
    return emptyStore();
  }
}

function writeStore(store: MappingProfileProjectTrustStore): void {
  if (getStorage()) {
    setDurableStateValue(PROFILE_TRUST_STORAGE_KEY, JSON.stringify(store));
  }
}

function isConvertedLegacyProfile(record: MappingProfileProjectTrustRecord): boolean {
  const settingsType = String(record.settingsType).trim().toLowerCase();
  return normalizeProfileKey(record.profileKey) === 'default (legacy)::1' &&
    (settingsType === '1' || settingsType === 'user');
}

function readTrustRecord(
  store: MappingProfileProjectTrustStore,
  trustKey: string | undefined
): MappingProfileProjectTrustRecord | null {
  const record = trustKey ? store.records[trustKey] : null;
  if (
    !record ||
    record.setupReadinessPolicy === 'legacy-conversion-bypass' ||
    !isConvertedLegacyProfile(record)
  ) {
    return record ?? null;
  }

  const migrated: MappingProfileProjectTrustRecord = {
    ...record,
    setupReadinessPolicy: 'legacy-conversion-bypass',
  };
  writeStore({ ...store, records: { ...store.records, [trustKey!]: migrated } });
  return migrated;
}

function claimLegacyBinding(
  userId: string,
  projectKey: string,
  store: MappingProfileProjectTrustStore
): MappingProfileProjectTrustRecord | null {
  const normalizedProjectKey = normalizeMappingProjectKey(projectKey);
  if (!normalizedProjectKey || store.legacyClaimedProjects[normalizedProjectKey]) {
    return null;
  }
  const legacyTrustKey = store.legacyProjectBindings[normalizedProjectKey];
  const legacyRecord = readTrustRecord(store, legacyTrustKey);
  if (!legacyRecord) {
    return null;
  }
  const trustKey = buildMappingProfileProjectTrustKey(userId, projectKey, legacyRecord.profileKey);
  const scopeKey = buildUserProjectScopeKey(userId, projectKey);
  if (!trustKey || !scopeKey) {
    return null;
  }
  const record: MappingProfileProjectTrustRecord = {
    ...legacyRecord,
    userId,
    setupCompleted: true,
  };
  writeStore({
    ...store,
    records: { ...store.records, [trustKey]: record },
    userProjectBindings: { ...store.userProjectBindings, [scopeKey]: trustKey },
    legacyClaimedProjects: {
      ...store.legacyClaimedProjects,
      [normalizedProjectKey]: true,
    },
  });
  return record;
}

export function readMappingProjectProfileBinding(
  userId: string | null | undefined,
  projectKey: string | null | undefined
): MappingProfileProjectTrustRecord | null {
  const normalizedUserId = normalizeUserProjectUserId(userId);
  const scopeKey = buildUserProjectScopeKey(userId, projectKey);
  if (!normalizedUserId || !scopeKey || !projectKey) {
    return null;
  }
  const store = readStore();
  const trustKey = store.userProjectBindings[scopeKey];
  const record = readTrustRecord(store, trustKey);
  return record
    ? { ...record, userId: normalizedUserId, setupCompleted: true }
    : claimLegacyBinding(normalizedUserId, projectKey, store);
}

export function readMappingProfileProjectTrust(
  userId: string | null | undefined,
  projectKey: string | null | undefined,
  profileKey: string | null | undefined
): MappingProfileProjectTrustRecord | null {
  const trustKey = buildMappingProfileProjectTrustKey(userId, projectKey, profileKey);
  if (!trustKey) {
    return null;
  }
  const direct = readTrustRecord(readStore(), trustKey);
  if (direct) {
    return {
      ...direct,
      userId: normalizeUserProjectUserId(userId) ?? direct.userId,
      setupCompleted: true,
    };
  }
  const claimed = readMappingProjectProfileBinding(userId, projectKey);
  return claimed && normalizeProfileKey(claimed.profileKey) === normalizeProfileKey(profileKey)
    ? claimed
    : null;
}

export function markMappingProfileProjectTrust(input: {
  userId: string | null | undefined;
  projectKey: string | null | undefined;
  profileKey: string | null | undefined;
  settingsType: SettingsType | number | string;
  documentName?: string | null;
  source: MappingProfileProjectTrustSource;
  setupReadinessPolicy?: MappingSetupReadinessPolicy;
}): MappingProfileProjectTrustRecord | null {
  const normalizedUserId = normalizeUserProjectUserId(input.userId);
  const trustKey = buildMappingProfileProjectTrustKey(
    input.userId,
    input.projectKey,
    input.profileKey
  );
  const scopeKey = buildUserProjectScopeKey(input.userId, input.projectKey);
  if (!normalizedUserId || !trustKey || !scopeKey || !input.projectKey || !input.profileKey) {
    return null;
  }
  const store = readStore();
  const record: MappingProfileProjectTrustRecord = {
    userId: normalizedUserId,
    projectKey: input.projectKey,
    setupCompleted: true,
    profileKey: input.profileKey,
    settingsType: input.settingsType,
    documentName: input.documentName ?? null,
    source: input.source,
    confirmedAt: new Date().toISOString(),
    setupReadinessPolicy:
      input.setupReadinessPolicy ??
      (store.records[trustKey]?.setupReadinessPolicy === 'legacy-conversion-bypass'
        ? 'legacy-conversion-bypass'
        : undefined),
    repairPersistence: store.records[trustKey]?.repairPersistence,
  };
  writeStore({
    ...store,
    records: { ...store.records, [trustKey]: record },
    userProjectBindings: { ...store.userProjectBindings, [scopeKey]: trustKey },
  });
  return record;
}

function updateRepairPersistence(
  userId: string | null | undefined,
  projectKey: string | null | undefined,
  status: MappingRepairPersistenceStatus,
  profileKey?: string | null
): MappingProfileProjectTrustRecord | null {
  const current = profileKey
    ? readMappingProfileProjectTrust(userId, projectKey, profileKey)
    : readMappingProjectProfileBinding(userId, projectKey);
  const trustKey = current
    ? buildMappingProfileProjectTrustKey(userId, projectKey, current.profileKey)
    : null;
  if (!current || !trustKey) {
    return null;
  }
  const store = readStore();
  const record: MappingProfileProjectTrustRecord = {
    ...current,
    repairPersistence: { status, updatedAt: new Date().toISOString() },
  };
  writeStore({ ...store, records: { ...store.records, [trustKey]: record } });
  return record;
}

export function markMappingRepairPendingSave(
  userId: string | null | undefined,
  projectKey: string | null | undefined,
  profileKey: string | null | undefined
): MappingProfileProjectTrustRecord | null {
  return updateRepairPersistence(userId, projectKey, 'pending-save', profileKey);
}

export function markMappingRepairDocumentSaved(
  userId: string | null | undefined,
  projectKey: string | null | undefined,
  previousProjectKey?: string | null
): MappingProfileProjectTrustRecord | null {
  return updateRepairPersistence(userId, projectKey, 'saved') ??
    updateRepairPersistence(userId, previousProjectKey, 'saved');
}

export function markMappingRepairDocumentClosing(
  userId: string | null | undefined,
  projectKey: string | null | undefined,
  isModified: boolean | null | undefined
): MappingProfileProjectTrustRecord | null {
  const binding = readMappingProjectProfileBinding(userId, projectKey);
  if (binding?.repairPersistence?.status !== 'pending-save' || typeof isModified !== 'boolean') {
    return binding;
  }
  return updateRepairPersistence(
    userId,
    projectKey,
    isModified ? 'discarded-unsaved' : 'saved'
  );
}

export function markMappingRepairVerifiedPresent(
  userId: string | null | undefined,
  projectKey: string | null | undefined,
  profileKey: string | null | undefined
): MappingProfileProjectTrustRecord | null {
  const trust = readMappingProfileProjectTrust(userId, projectKey, profileKey);
  const status = trust?.repairPersistence?.status;
  return trust && status !== 'pending-save' && status !== 'saved'
    ? updateRepairPersistence(userId, projectKey, 'saved', profileKey)
    : trust;
}
